/**
 * Browser-compatible JWT utilities
 * Replaces server-side jsonwebtoken library
 */

export interface JWTPayload {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  'custom:user_type'?: string;
  'cognito:groups'?: string[];
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  'custom:full_name'?: string;
  iat?: number;
  exp?: number;
  aud?: string;
  iss?: string;
  [key: string]: any; // Allow additional custom fields
}

/**
 * Decode JWT token without verification (client-side only)
 * Note: This does NOT verify the signature - only use for reading claims
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    
    // Decode base64url
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decodedPayload) as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isJWTExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  return payload.exp * 1000 < Date.now();
};

/**
 * Get JWT expiration time
 */
export const getJWTExpiration = (token: string): number | null => {
  const payload = decodeJWT(token);
  return payload?.exp ? payload.exp * 1000 : null;
};

/**
 * Extract user information from ID token
 */
export const extractUserFromIDToken = (idToken: string) => {
  const payload = decodeJWT(idToken);
  if (!payload) return null;

  return {
    sub: payload.sub || '',
    email: payload.email || '',
    email_verified: payload.email_verified || false,
    'custom:user_type': (payload['custom:user_type'] as 'individual' | 'fleet' | 'staff' | 'admin') || 'individual',
    'cognito:groups': payload['cognito:groups'] || [],
    given_name: payload.given_name,
    family_name: payload.family_name,
    phone_number: payload.phone_number,
    'custom:full_name': payload['custom:full_name'] || `${payload.given_name || ''} ${payload.family_name || ''}`.trim(),
  };
};
